import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  PlusCircle,
  ExternalLink,
  FileText,
  Eye,
  Tag,
  Paperclip,
  Hourglass,
  Send,
} from 'lucide-react';
import { formatDateTimeIST } from '@/lib/dateFormat';
import { leaveService } from '@/services/leave.service';
import { ApplyLeaveModal } from './ApplyLeaveModal';
import { ShortLeaveModal } from './ShortLeaveModal';
import { toast } from 'sonner';
import type { LeaveRequest, ShortLeaveRequest } from '@pms/shared';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type LeaveTab = 'regular' | 'short';

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const MAIN_TABS: { key: LeaveTab; label: string }[] = [
  { key: 'regular', label: 'Regular Leave' },
  { key: 'short',   label: 'Short Leave' },
];

const LIMIT = 10;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(t: string) {
  const parts = t.split(':');
  const hour = parseInt(parts[0] ?? '0', 10);
  const m = parts[1] ?? '00';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${suffix}`;
}

function formatDuration(mins: number) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: LeaveRequest['status'] }) {
  const map = {
    pending:   { label: 'Pending',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    approved:  { label: 'Approved',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    rejected:  { label: 'Rejected',  cls: 'bg-red-50 text-red-700 border-red-200' },
    cancelled: { label: 'Cancelled', cls: 'bg-muted text-muted-foreground border-border' },
  } as const;
  const v = map[status] ?? map.cancelled;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${v.cls}`}>
      {v.label}
    </span>
  );
}

interface ApprovalStepBadgeProps {
  total: number;
  status: LeaveRequest['status'];
  history: { step_order: number; action: 'approved' | 'rejected' }[];
  cancelReason?: string | null;
}

function ApprovalStepBadge({ total, status, history, cancelReason }: ApprovalStepBadgeProps) {
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
        <CheckCircle2 className="w-3 h-3" />
        All {total} step{total !== 1 ? 's' : ''}
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
        <XCircle className="w-3 h-3" />
        Rejected
      </span>
    );
  }
  if (status === 'cancelled') {
    return <span className="text-xs text-muted-foreground">{cancelReason || '—'}</span>;
  }
  if (Number(total) <= 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-medium">
        Pending approval
      </span>
    );
  }
  const completedSteps = history.filter((h) => h.action === 'approved').length;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-medium">
      Step {completedSteps + 1}/{total}
    </span>
  );
}

// ── RegularLeaveCard ──────────────────────────────────────────────────────────

function RegularLeaveCard({
  req,
  onCancel,
  onView,
  onEdit,
}: {
  req: LeaveRequest;
  onCancel: (id: string) => void;
  onView: (r: LeaveRequest) => void;
  onEdit: (r: LeaveRequest) => void;
}) {
  const sameDay = req.start_date === req.end_date;
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2.5">
      {/* Type + status + view */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: req.leave_type_color ?? '#6B7280' }}
          />
          <span className="font-medium truncate">{req.leave_type_name ?? '—'}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <StatusBadge status={req.status} />
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onView(req)}>
            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Date range + day count + half-day */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">
          {sameDay
            ? formatDate(req.start_date)
            : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`}
        </span>
        <span className="text-xs font-medium bg-muted px-2 py-0.5 rounded-full">
          {req.days_count}d
        </span>
        {req.is_half_day && (
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full capitalize">
            {req.half_day_period ?? 'half-day'}
          </span>
        )}
      </div>

      {/* Reason */}
      <p className="text-sm text-muted-foreground line-clamp-2">{req.reason}</p>

      {/* Approval step + cancel */}
      <div className="flex items-center justify-between gap-2 pt-0.5">
        <ApprovalStepBadge
          total={req.total_approval_steps ?? 1}
          status={req.status}
          history={req.approval_history ?? []}
          cancelReason={req.cancel_reason}
        />
        {req.status === 'pending' && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => onEdit(req)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-3 text-xs"
              onClick={() => onCancel(req.id)}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ShortLeaveCard ────────────────────────────────────────────────────────────

function ShortLeaveCard({
  req,
  onCancel,
  onView,
  onEdit,
}: {
  req: ShortLeaveRequest;
  onCancel: (id: string) => void;
  onView: (r: ShortLeaveRequest) => void;
  onEdit: (r: ShortLeaveRequest) => void;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2.5">
      {/* Date + status + view */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">
          {new Date(req.request_date).toLocaleDateString('en-GB', {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
          })}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <StatusBadge status={req.status} />
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onView(req)}>
            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Time range + duration */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {formatTime(req.from_time)} → {formatTime(req.to_time)}
        </span>
        <span className="text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full">
          {formatDuration(req.duration_minutes)}
        </span>
      </div>

      {/* Reason */}
      <p className="text-sm text-muted-foreground line-clamp-2">{req.reason}</p>

      {/* Edit / Cancel */}
      {req.status === 'pending' && (
        <div className="flex items-center justify-end gap-1 pt-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => onEdit(req)}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-3 text-xs"
            onClick={() => onCancel(req.id)}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Detail view building blocks ───────────────────────────────────────────────

function DetailTile({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1 rounded-lg border border-border/60 bg-muted/30 p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </p>
      <div className="text-sm font-medium text-foreground break-words">{children}</div>
    </div>
  );
}

type TimelineTone = 'applied' | 'approved' | 'rejected' | 'cancelled' | 'waiting';

interface TimelineEntry {
  tone: TimelineTone;
  title: string;
  time?: string;
  note?: string | null;
}

const TIMELINE_DOT: Record<TimelineTone, string> = {
  applied:   'bg-sky-500',
  approved:  'bg-emerald-500',
  rejected:  'bg-red-500',
  cancelled: 'bg-muted-foreground',
  waiting:   'border-2 border-amber-500 bg-background',
};

function StatusTimeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Status Timeline</p>
      <ol>
        {entries.map((e, i) => (
          <li key={i} className="relative flex gap-3 pb-4 last:pb-0">
            {i < entries.length - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-border" />}
            <span className={`relative mt-1 h-3 w-3 shrink-0 rounded-full ${TIMELINE_DOT[e.tone]}`} />
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm font-medium">{e.title}</p>
              {e.time && <p className="text-xs text-muted-foreground">{e.time}</p>}
              {e.note && <p className="text-xs italic text-muted-foreground break-words">"{e.note}"</p>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ReasonBox({ reason }: { reason: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Reason</p>
      <p className="rounded-lg bg-muted/40 p-3 text-sm leading-relaxed whitespace-pre-wrap break-words">{reason}</p>
    </div>
  );
}

function personName(first: string | null | undefined, last: string | null | undefined) {
  return first ? `${first} ${last ?? ''}`.trim() : null;
}

// ── LeaveDetailModal ──────────────────────────────────────────────────────────

function LeaveDetailModal({
  req,
  onClose,
  onCancel,
  onEdit,
}: {
  req: LeaveRequest | null;
  onClose: () => void;
  onCancel: (id: string) => void;
  onEdit: (r: LeaveRequest) => void;
}) {
  if (!req) return null;
  const sameDay = req.start_date === req.end_date;
  const history = req.approval_history ?? [];
  const totalSteps = Number(req.total_approval_steps ?? 1);
  const approvedSteps = history.filter((h) => h.action === 'approved').length;

  const timeline: TimelineEntry[] = [
    { tone: 'applied', title: 'Applied', time: formatDateTimeIST(req.created_at) },
    ...history.map((h): TimelineEntry => {
      const by = personName(h.approver_first_name, h.approver_last_name);
      return {
        tone: h.action === 'approved' ? 'approved' : 'rejected',
        title: `Step ${h.step_order} · ${h.action === 'approved' ? 'Approved' : 'Rejected'}${by ? ` by ${by}` : ''}`,
        time: formatDateTimeIST(h.acted_at),
        note: h.remarks,
      };
    }),
  ];
  if (req.status === 'cancelled') {
    timeline.push({
      tone: 'cancelled',
      title: 'Cancelled',
      time: formatDateTimeIST(req.cancelled_at ?? req.updated_at),
      note: req.cancel_reason,
    });
  }
  if (req.status === 'pending') {
    timeline.push({
      tone: 'waiting',
      title: totalSteps > 0
        ? `Waiting for approval · step ${approvedSteps + 1} of ${totalSteps}`
        : 'Waiting for approval',
    });
  }

  return (
    <Dialog open={!!req} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 py-4 border-b border-border/60 space-y-1 text-left">
          <div className="flex items-center justify-between gap-3 pr-6">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: req.leave_type_color ?? '#6B7280' }}
              />
              <DialogTitle className="text-base font-semibold leading-snug truncate">
                {req.leave_type_name ?? 'Leave Request'}
              </DialogTitle>
            </div>
            <StatusBadge status={req.status} />
          </div>
          <p className="text-xs text-muted-foreground">Applied on {formatDateTimeIST(req.created_at)}</p>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <DetailTile icon={<Calendar className="w-3.5 h-3.5" />} label={sameDay ? 'Leave Date' : 'Leave Dates'}>
              {sameDay ? formatDate(req.start_date) : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`}
            </DetailTile>
            <DetailTile icon={<Clock className="w-3.5 h-3.5" />} label="Duration">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span>{req.days_count} day{Number(req.days_count) !== 1 ? 's' : ''}</span>
                {req.is_half_day && (
                  <span className="text-xs font-normal bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full capitalize dark:bg-purple-500/10 dark:text-purple-300 dark:border-purple-500/30">
                    {req.half_day_period ?? 'half-day'}
                  </span>
                )}
              </div>
            </DetailTile>
            <DetailTile icon={<Tag className="w-3.5 h-3.5" />} label="Leave Type">
              {req.leave_type_name ?? '—'}
            </DetailTile>
            <DetailTile icon={<Paperclip className="w-3.5 h-3.5" />} label="Document">
              {req.document_url ? (
                <a
                  href={req.document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  View Document
                </a>
              ) : (
                <span className="font-normal text-muted-foreground">Not attached</span>
              )}
            </DetailTile>
          </div>

          <ReasonBox reason={req.reason} />

          <StatusTimeline entries={timeline} />
        </div>

        {req.status === 'pending' && (
          <div className="px-5 py-4 border-t border-border/60 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { onEdit(req); onClose(); }}
            >
              Edit Request
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { onCancel(req.id); onClose(); }}
            >
              Cancel Request
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── ShortLeaveDetailModal ─────────────────────────────────────────────────────

function ShortLeaveDetailModal({
  req,
  onClose,
  onCancel,
  onEdit,
}: {
  req: ShortLeaveRequest | null;
  onClose: () => void;
  onCancel: (id: string) => void;
  onEdit: (r: ShortLeaveRequest) => void;
}) {
  if (!req) return null;

  const approver = personName(req.approver_first_name, req.approver_last_name);
  const timeline: TimelineEntry[] = [
    { tone: 'applied', title: 'Applied', time: formatDateTimeIST(req.created_at) },
  ];
  if (req.status === 'approved') {
    timeline.push({
      tone: 'approved',
      title: `Approved${approver ? ` by ${approver}` : ''}`,
      time: formatDateTimeIST(req.approved_at ?? req.updated_at),
    });
  } else if (req.status === 'rejected') {
    timeline.push({ tone: 'rejected', title: 'Rejected', time: formatDateTimeIST(req.updated_at), note: req.rejection_reason });
  } else if (req.status === 'cancelled') {
    timeline.push({ tone: 'cancelled', title: 'Cancelled', time: formatDateTimeIST(req.updated_at) });
  } else {
    timeline.push({ tone: 'waiting', title: 'Waiting for approval' });
  }

  return (
    <Dialog open={!!req} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 py-4 border-b border-border/60 space-y-1 text-left">
          <div className="flex items-center justify-between gap-3 pr-6">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 rounded-full shrink-0 bg-teal-500" />
              <DialogTitle className="text-base font-semibold leading-snug">Short Leave</DialogTitle>
            </div>
            <StatusBadge status={req.status} />
          </div>
          <p className="text-xs text-muted-foreground">Applied on {formatDateTimeIST(req.created_at)}</p>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <DetailTile icon={<Calendar className="w-3.5 h-3.5" />} label="Date">
              {new Date(req.request_date).toLocaleDateString('en-GB', {
                weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
              })}
            </DetailTile>
            <DetailTile icon={<Clock className="w-3.5 h-3.5" />} label="Time">
              {formatTime(req.from_time)} → {formatTime(req.to_time)}
            </DetailTile>
            <DetailTile icon={<Hourglass className="w-3.5 h-3.5" />} label="Duration">
              <span className="inline-flex text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/30">
                {formatDuration(req.duration_minutes)}
              </span>
            </DetailTile>
            <DetailTile icon={<Send className="w-3.5 h-3.5" />} label="Applied On">
              {formatDateTimeIST(req.created_at)}
            </DetailTile>
          </div>

          <ReasonBox reason={req.reason} />

          <StatusTimeline entries={timeline} />
        </div>

        {req.status === 'pending' && (
          <div className="px-5 py-4 border-t border-border/60 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { onEdit(req); onClose(); }}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { onCancel(req.id); onClose(); }}
            >
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── LeavePage ─────────────────────────────────────────────────────────────────

export default function LeavePage() {
  const queryClient = useQueryClient();
  const [leaveTab, setLeaveTab]             = useState<LeaveTab>('regular');
  const [regularStatus, setRegularStatus]   = useState<StatusFilter>('all');
  const [shortStatus, setShortStatus]       = useState<StatusFilter>('all');
  const [applyOpen, setApplyOpen]           = useState(false);
  const [shortOpen, setShortOpen]           = useState(false);
  const [regularPage, setRegularPage]       = useState(1);
  const [shortPage, setShortPage]           = useState(1);
  const [viewLeave, setViewLeave]           = useState<LeaveRequest | null>(null);
  const [viewShortLeave, setViewShortLeave] = useState<ShortLeaveRequest | null>(null);
  const [editRequest, setEditRequest]       = useState<LeaveRequest | null>(null);
  const [editShortLeave, setEditShortLeave] = useState<ShortLeaveRequest | null>(null);

  const activeStatus = leaveTab === 'regular' ? regularStatus : shortStatus;

  const requestsQuery = useQuery({
    queryKey: ['leave', 'requests', 'me', regularStatus, regularPage],
    queryFn: () =>
      leaveService.listMyRequests({
        status: regularStatus === 'all' ? undefined : regularStatus,
        page: regularPage,
        limit: LIMIT,
      }),
  });

  const shortQuery = useQuery({
    queryKey: ['leave', 'short', 'me', shortStatus, shortPage],
    queryFn: () =>
      leaveService.listMyShortLeaves({
        status: shortStatus === 'all' ? undefined : shortStatus,
        page: shortPage,
        limit: LIMIT,
      }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => leaveService.cancelLeaveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      toast.success('Leave request cancelled');
    },
    onError: () => toast.error('Failed to cancel request'),
  });

  const cancelShortMutation = useMutation({
    mutationFn: (id: string) => leaveService.cancelShortLeave(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      toast.success('Short leave cancelled');
    },
    onError: () => toast.error('Failed to cancel short leave'),
  });

  const leaveColumns: Column<LeaveRequest>[] = [
    {
      key: 'type',
      header: 'Leave Type',
      cell: (req) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: req.leave_type_color ?? '#6B7280' }} />
          <span className="font-medium truncate">{req.leave_type_name ?? '—'}</span>
        </div>
      ),
    },
    {
      key: 'period',
      header: 'Period',
      cell: (req) => {
        const sameDay = req.start_date === req.end_date;
        return (
          <div className="space-y-0.5">
            <p className="text-sm whitespace-nowrap">
              {sameDay ? formatDate(req.start_date) : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-medium bg-muted px-2 py-0.5 rounded-full">{req.days_count}d</span>
              {req.is_half_day && (
                <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full capitalize">
                  {req.half_day_period ?? 'half-day'}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (req) => <span className="text-sm text-muted-foreground line-clamp-2 max-w-[180px]">{req.reason}</span>,
    },
    {
      key: 'doc',
      header: 'Doc',
      cell: (req) =>
        req.document_url ? (
          <a href={req.document_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : (
          <FileText className="w-4 h-4 text-muted-foreground/40" />
        ),
    },
    {
      key: 'approval',
      header: 'Approval',
      cell: (req) => (
        <ApprovalStepBadge
          total={req.total_approval_steps ?? 1}
          status={req.status}
          history={req.approval_history ?? []}
          cancelReason={req.cancel_reason}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (req) => <StatusBadge status={req.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-40 text-right',
      cell: (req) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewLeave(req)}>
            <Eye className="h-3.5 w-3.5" />
          </Button>
          {req.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={() => setEditRequest(req)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-3 text-xs"
                onClick={() => cancelMutation.mutate(req.id)}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const shortColumns: Column<ShortLeaveRequest>[] = [
    {
      key: 'date',
      header: 'Date',
      cell: (sl) => <span className="whitespace-nowrap">{formatDate(sl.request_date)}</span>,
    },
    {
      key: 'time',
      header: 'Time',
      cell: (sl) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm whitespace-nowrap">{formatTime(sl.from_time)} → {formatTime(sl.to_time)}</span>
          <span className="text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full whitespace-nowrap">
            {formatDuration(sl.duration_minutes)}
          </span>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (sl) => <span className="text-sm text-muted-foreground line-clamp-2 max-w-[200px]">{sl.reason}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (sl) => <StatusBadge status={sl.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-40 text-right',
      cell: (sl) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setViewShortLeave(sl)}>
            <Eye className="h-3.5 w-3.5" />
          </Button>
          {sl.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={() => setEditShortLeave(sl)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-3 text-xs"
                onClick={() => cancelShortMutation.mutate(sl.id)}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  function handleMainTabChange(tab: string) {
    setLeaveTab(tab as LeaveTab);
  }

  function handleStatusTabChange(tab: string) {
    if (leaveTab === 'regular') {
      setRegularStatus(tab as StatusFilter);
      setRegularPage(1);
    } else {
      setShortStatus(tab as StatusFilter);
      setShortPage(1);
    }
  }

  const leavePagination = requestsQuery.data?.pagination;
  const shortPagination = shortQuery.data?.pagination;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Leave Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Apply for leaves and track your requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShortOpen(true)}>
            <Clock className="w-4 h-4 mr-2" />
            Short Leave
          </Button>
          <Button onClick={() => setApplyOpen(true)}>
            <PlusCircle className="w-4 h-4 mr-2" />
            Apply Leave
          </Button>
        </div>
      </div>

      {/* Main tabs: Regular / Short */}
      <Tabs tabs={MAIN_TABS} active={leaveTab} onChange={handleMainTabChange} />

      {/* Status filter tabs */}
      <Tabs tabs={STATUS_TABS} active={activeStatus} onChange={handleStatusTabChange} />

      {/* ── Regular Leave content ── */}
      {leaveTab === 'regular' && (
        <>
          {/* Desktop: DataTable */}
          <div className="hidden md:block">
            <DataTable
              columns={leaveColumns}
              rows={requestsQuery.data?.data ?? []}
              rowKey={(r) => r.id}
              loading={requestsQuery.isLoading}
              error={requestsQuery.isError}
              onRetry={() => void requestsQuery.refetch()}
              empty={
                <div className="flex flex-col items-center gap-3 py-12">
                  <Calendar className="w-10 h-10 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-muted-foreground">
                    {regularStatus === 'all' ? 'No leave requests yet' : `No ${regularStatus} requests`}
                  </p>
                  <Button size="sm" onClick={() => setApplyOpen(true)}>Apply Leave</Button>
                </div>
              }
              pagination={
                leavePagination && leavePagination.totalPages > 1
                  ? { page: regularPage, totalPages: leavePagination.totalPages, total: leavePagination.total, onPageChange: setRegularPage }
                  : undefined
              }
            />
          </div>

          {/* Mobile: Cards */}
          <div className="block md:hidden">
            {requestsQuery.isLoading && (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-28 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            )}

            {requestsQuery.isError && !requestsQuery.isLoading && (
              <div className="py-8 text-center text-sm text-destructive">
                Failed to load.{' '}
                <button className="underline" onClick={() => void requestsQuery.refetch()}>Retry</button>
              </div>
            )}

            {!requestsQuery.isLoading && !requestsQuery.isError && (
              <>
                {(requestsQuery.data?.data ?? []).length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-12">
                    <Calendar className="w-10 h-10 text-muted-foreground/40" />
                    <p className="text-sm font-medium text-muted-foreground">
                      {regularStatus === 'all' ? 'No leave requests yet' : `No ${regularStatus} requests`}
                    </p>
                    <Button size="sm" onClick={() => setApplyOpen(true)}>Apply Leave</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {requestsQuery.data!.data.map((req) => (
                      <RegularLeaveCard
                        key={req.id}
                        req={req}
                        onCancel={(id) => cancelMutation.mutate(id)}
                        onView={setViewLeave}
                        onEdit={setEditRequest}
                      />
                    ))}
                  </div>
                )}

                {leavePagination && leavePagination.totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <Button
                      variant="outline" size="sm"
                      disabled={regularPage <= 1}
                      onClick={() => setRegularPage((p) => p - 1)}
                    >
                      ← Prev
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Page {regularPage} of {leavePagination.totalPages}
                    </span>
                    <Button
                      variant="outline" size="sm"
                      disabled={regularPage >= leavePagination.totalPages}
                      onClick={() => setRegularPage((p) => p + 1)}
                    >
                      Next →
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Short Leave content ── */}
      {leaveTab === 'short' && (
        <>
          {/* Desktop: DataTable */}
          <div className="hidden md:block">
            <DataTable
              columns={shortColumns}
              rows={shortQuery.data?.data ?? []}
              rowKey={(r) => r.id}
              loading={shortQuery.isLoading}
              error={shortQuery.isError}
              onRetry={() => void shortQuery.refetch()}
              empty={
                <div className="flex flex-col items-center gap-3 py-12">
                  <Clock className="w-10 h-10 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-muted-foreground">
                    {shortStatus === 'all' ? 'No short leave requests yet' : `No ${shortStatus} short leaves`}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setShortOpen(true)}>Apply Short Leave</Button>
                </div>
              }
              pagination={
                shortPagination && shortPagination.totalPages > 1
                  ? { page: shortPage, totalPages: shortPagination.totalPages, total: shortPagination.total, onPageChange: setShortPage }
                  : undefined
              }
            />
          </div>

          {/* Mobile: Cards */}
          <div className="block md:hidden">
            {shortQuery.isLoading && (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            )}

            {shortQuery.isError && !shortQuery.isLoading && (
              <div className="py-8 text-center text-sm text-destructive">
                Failed to load.{' '}
                <button className="underline" onClick={() => void shortQuery.refetch()}>Retry</button>
              </div>
            )}

            {!shortQuery.isLoading && !shortQuery.isError && (
              <>
                {(shortQuery.data?.data ?? []).length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-12">
                    <Clock className="w-10 h-10 text-muted-foreground/40" />
                    <p className="text-sm font-medium text-muted-foreground">
                      {shortStatus === 'all' ? 'No short leave requests yet' : `No ${shortStatus} short leaves`}
                    </p>
                    <Button size="sm" variant="outline" onClick={() => setShortOpen(true)}>
                      Apply Short Leave
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {shortQuery.data!.data.map((req) => (
                      <ShortLeaveCard
                        key={req.id}
                        req={req}
                        onCancel={(id) => cancelShortMutation.mutate(id)}
                        onView={setViewShortLeave}
                        onEdit={setEditShortLeave}
                      />
                    ))}
                  </div>
                )}

                {shortPagination && shortPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <Button
                      variant="outline" size="sm"
                      disabled={shortPage <= 1}
                      onClick={() => setShortPage((p) => p - 1)}
                    >
                      ← Prev
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Page {shortPage} of {shortPagination.totalPages}
                    </span>
                    <Button
                      variant="outline" size="sm"
                      disabled={shortPage >= shortPagination.totalPages}
                      onClick={() => setShortPage((p) => p + 1)}
                    >
                      Next →
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      <ApplyLeaveModal
        key={editRequest?.id ?? 'new-leave'}
        open={applyOpen || !!editRequest}
        request={editRequest ?? undefined}
        onClose={() => { setApplyOpen(false); setEditRequest(null); }}
      />
      <ShortLeaveModal
        key={editShortLeave?.id ?? 'new-short'}
        open={shortOpen || !!editShortLeave}
        request={editShortLeave ?? undefined}
        onClose={() => { setShortOpen(false); setEditShortLeave(null); }}
      />

      <LeaveDetailModal
        req={viewLeave}
        onClose={() => setViewLeave(null)}
        onCancel={(id) => cancelMutation.mutate(id)}
        onEdit={setEditRequest}
      />
      <ShortLeaveDetailModal
        req={viewShortLeave}
        onClose={() => setViewShortLeave(null)}
        onCancel={(id) => cancelShortMutation.mutate(id)}
        onEdit={setEditShortLeave}
      />
    </div>
  );
}
